import { createEditor, Descendant } from 'slate';
import { withReact } from 'slate-react';
import { withHistory } from 'slate-history';

export const createLiveEditor = () => withHistory(withReact(createEditor()));
type Editor = ReturnType<typeof createLiveEditor>;
type Snapshot = Pick<Editor, 'children' | 'selection' | 'history'>;
export type LiveEditorCache = Map<string, Snapshot | null>;

export const restoreLiveEditor = (
  cache: LiveEditorCache,
  key: string,
  children: Descendant[]
) => {
  const saved = cache.get(key);
  const editor = createLiveEditor();
  if (saved) {
    editor.selection = structuredClone(saved.selection);
    editor.history = structuredClone(saved.history);
  }
  return { editor, initialValue: structuredClone(saved?.children ?? children) };
};

export const registerLiveEditor = (cache: LiveEditorCache, key: string) => {
  const prefix = key.slice(0, key.lastIndexOf(':') + 1);
  for (const previous of cache.keys()) {
    if (previous.startsWith(prefix) && previous !== key) cache.delete(previous);
  }
  // Register after mount; render initializers can run twice in StrictMode.
  cache.set(key, null);
};

export const retainLiveEditor = (
  cache: LiveEditorCache,
  key: string,
  editor: Editor
) => {
  if (!cache.has(key)) return; // A newer server revision superseded this editor.
  if (
    !editor.selection &&
    !editor.history.undos.length &&
    !editor.history.redos.length
  ) {
    cache.delete(key);
    return;
  }
  // Slate's node-to-DOM weak maps must not share keys with retained data.
  cache.set(
    key,
    structuredClone({
      children: editor.children,
      selection: editor.selection,
      history: editor.history
    })
  );
};
